import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { documentsTemplatePageSchema, documentsTemplateSensitivitySchema, documentsTemplateVariableKeySchema, documentsTemplateVariableValueSchema, } from './documents-template.contract.js';
export const documentsInstancesCapability = 'documents.instances.v1';
export const documentsSignaturesCapability = 'documents.signatures.v1';
export const documentsAuthenticityCapability = 'documents.authenticity.v1';
export const documentsInstanceIdSchema = z.string().uuid();
export const documentsInstancePublicIdSchema = z.string().uuid();
export const documentsInstanceCommandIdSchema = z.string().uuid();
export const documentsInstanceRevisionSchema = z.number().int().positive();
export const documentsInstanceStatusSchema = z.enum([
    'draft', 'sent', 'viewed', 'signed', 'declined', 'voided', 'expired',
]);
export const documentsInstanceReasonSchema = z.string().trim().min(5).max(500);
export const documentsSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const documentsInstanceRecipientSchema = z.strictObject({
    name: z.string().trim().min(1).max(240),
    email: z.string().trim().email().max(320).nullable(),
    phone: z.string().trim().min(3).max(40).nullable(),
});
export const documentsInstanceSourceReferenceSchema = z.strictObject({
    capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
    resourceType: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    resourceId: z.string().trim().min(1).max(240),
    displayLabel: z.string().trim().min(1).max(240).nullable(),
});
export const documentsInstanceTemplateSnapshotSchema = z.strictObject({
    templateId: z.string().uuid(),
    templateKey: z.string().trim().min(1).max(100),
    templateName: z.string().trim().min(1).max(180),
    templateVersionId: z.string().uuid(),
    templateVersionNumber: z.number().int().positive(),
    templateContentHash: documentsSha256Schema,
    requiresSignature: z.boolean(),
    consentText: z.string().trim().min(1).max(4_000).nullable(),
    sensitivity: documentsTemplateSensitivitySchema,
    page: documentsTemplatePageSchema,
});
export const documentsInstanceRenderSnapshotSchema = z.strictObject({
    values: z.record(documentsTemplateVariableKeySchema, documentsTemplateVariableValueSchema),
    renderedHtml: z.string().trim().min(1).max(1_000_000),
    renderedHtmlSha256: documentsSha256Schema,
    printableHtml: z.string().trim().min(1).max(1_500_000),
    printableHtmlSha256: documentsSha256Schema,
});
export const documentsSignatureKindSchema = z.enum(['typed', 'drawn', 'typed-and-drawn']);
export const documentsSignatureSchema = z.strictObject({
    id: z.string().uuid(),
    instanceId: documentsInstanceIdSchema,
    kind: documentsSignatureKindSchema,
    typedName: z.string().trim().min(1).max(240),
    signatureSha256: documentsSha256Schema,
    signatureArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable(),
    consentText: z.string().trim().min(1).max(4_000),
    consentAcceptedAt: z.string().datetime({ offset: true }),
    networkAddressSha256: documentsSha256Schema.nullable(),
    userAgentSha256: documentsSha256Schema.nullable(),
    signedByMembershipId: z.string().uuid().nullable(),
    actorPublicLinkId: z.string().uuid().nullable().default(null),
    createdAt: z.string().datetime({ offset: true }),
});
export const documentsInstanceSchema = z.strictObject({
    contractVersion: z.literal(documentsInstancesCapability),
    tenantId: z.string().uuid(),
    id: documentsInstanceIdSchema,
    publicId: documentsInstancePublicIdSchema,
    status: documentsInstanceStatusSchema,
    revision: documentsInstanceRevisionSchema,
    template: documentsInstanceTemplateSnapshotSchema,
    render: documentsInstanceRenderSnapshotSchema,
    recipient: documentsInstanceRecipientSchema,
    sourceReference: documentsInstanceSourceReferenceSchema.nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    releasedAt: z.string().datetime({ offset: true }).nullable(),
    firstViewedAt: z.string().datetime({ offset: true }).nullable(),
    signedAt: z.string().datetime({ offset: true }).nullable(),
    declinedAt: z.string().datetime({ offset: true }).nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    expiredAt: z.string().datetime({ offset: true }).nullable(),
    finalArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable(),
    finalArtifactSha256: documentsSha256Schema.nullable(),
    signature: documentsSignatureSchema.nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const documentsInstanceEventOperationSchema = z.enum([
    'create-draft', 'send', 'view', 'remind', 'void', 'decline', 'expire', 'sign',
    'finalize-artifact',
]);
export const documentsInstanceEventSchema = z.strictObject({
    id: z.string().uuid(),
    instanceId: documentsInstanceIdSchema,
    operation: documentsInstanceEventOperationSchema,
    previousStatus: documentsInstanceStatusSchema.nullable(),
    nextStatus: documentsInstanceStatusSchema,
    revisionAfter: documentsInstanceRevisionSchema,
    reason: documentsInstanceReasonSchema,
    actorMembershipId: z.string().uuid(),
    actorPublicLinkId: z.string().uuid().nullable().default(null),
    occurredAt: z.string().datetime({ offset: true }),
});
export const documentsInstanceListQuerySchema = z.strictObject({
    status: documentsInstanceStatusSchema.nullable().default(null),
    templateId: z.string().uuid().nullable().default(null),
    sourceResourceId: z.string().trim().min(1).max(240).nullable().default(null),
    query: z.string().trim().max(160).default(''),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const documentsCreateDraftSchema = z.strictObject({
    commandId: documentsInstanceCommandIdSchema,
    templateId: z.string().uuid(),
    values: z.record(documentsTemplateVariableKeySchema, documentsTemplateVariableValueSchema).default({}),
    recipient: documentsInstanceRecipientSchema,
    sourceReference: documentsInstanceSourceReferenceSchema.nullable().default(null),
    expiresAt: z.string().datetime({ offset: true }),
    reason: documentsInstanceReasonSchema,
});
const documentsInstanceStateCommandBaseSchema = z.strictObject({
    commandId: documentsInstanceCommandIdSchema,
    expectedRevision: documentsInstanceRevisionSchema,
    reason: documentsInstanceReasonSchema,
});
export const documentsSendInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('send'),
});
export const documentsViewInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('view'),
});
export const documentsRemindInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('remind'),
    message: z.string().trim().min(1).max(1_000).nullable().default(null),
});
export const documentsVoidInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('void'),
});
export const documentsDeclineInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('decline'),
});
export const documentsExpireInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('expire'),
});
export const documentsSignInstanceSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('sign'),
    signature: z.strictObject({
        kind: documentsSignatureKindSchema,
        typedName: z.string().trim().min(1).max(240),
        signatureSha256: documentsSha256Schema,
        signatureArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable().default(null),
        consentAccepted: z.literal(true),
        networkAddressSha256: documentsSha256Schema.nullable().default(null),
        userAgentSha256: documentsSha256Schema.nullable().default(null),
    }),
});
export const documentsFinalizeArtifactSchema = documentsInstanceStateCommandBaseSchema.extend({
    kind: z.literal('finalize-artifact'),
    artifact: platformStorageAttachedObjectReferenceSchema,
});
export const documentsInstanceCommandSchema = z.discriminatedUnion('kind', [
    documentsSendInstanceSchema,
    documentsViewInstanceSchema,
    documentsRemindInstanceSchema,
    documentsVoidInstanceSchema,
    documentsDeclineInstanceSchema,
    documentsExpireInstanceSchema,
    documentsSignInstanceSchema,
    documentsFinalizeArtifactSchema,
]);
export const documentsPrintablePreviewRequestSchema = z.strictObject({
    templateId: z.string().uuid(),
    values: z.record(documentsTemplateVariableKeySchema, documentsTemplateVariableValueSchema).default({}),
    title: z.string().trim().min(1).max(180).nullable().default(null),
});
export const documentsPrintableRenderSchema = z.strictObject({
    htmlDocument: z.string().trim().min(1).max(1_500_000),
    bodyHtml: z.string().trim().min(1).max(1_000_000),
    templateContentHash: documentsSha256Schema,
    renderedHtmlSha256: documentsSha256Schema,
    printableHtmlSha256: documentsSha256Schema,
    missingRequiredVariables: z.array(documentsTemplateVariableKeySchema),
    page: documentsTemplatePageSchema,
    contentType: z.literal('text/html; charset=utf-8'),
    writesDocumentInstance: z.literal(false),
});
export const documentsPrintablePreviewResponseSchema = z.strictObject({
    data: documentsPrintableRenderSchema,
});
export const documentsAuthenticitySchema = z.strictObject({
    contractVersion: z.literal(documentsAuthenticityCapability),
    publicId: documentsInstancePublicIdSchema,
    templateKey: z.string().trim().min(1).max(100),
    status: documentsInstanceStatusSchema,
    signedAt: z.string().datetime({ offset: true }).nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    renderedHtmlSha256: documentsSha256Schema,
    finalArtifactSha256: documentsSha256Schema.nullable(),
});
const documentsCommandMetadataSchema = z.strictObject({
    commandId: documentsInstanceCommandIdSchema,
    idempotent: z.boolean(),
});
export const documentsInstanceResponseSchema = z.strictObject({
    data: documentsInstanceSchema,
    command: documentsCommandMetadataSchema.optional(),
});
export const documentsInstancesResponseSchema = z.strictObject({
    data: z.array(documentsInstanceSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const documentsInstanceEventsResponseSchema = z.strictObject({
    data: z.array(documentsInstanceEventSchema),
});
export const documentsAuthenticityResponseSchema = z.strictObject({
    data: documentsAuthenticitySchema,
});
//# sourceMappingURL=documents-instance.contract.js.map